import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const LIST_URL = "/api/v1/inventory-sku-mapping";
const STATS_URL = "/api/v1/inventory-sku-mapping/stats";
const patchMappingUrl = (id) => `/api/v1/inventory-sku-mapping/${id}`;
const CATALOG_SKUS_URL = "/api/v1/product-catalog/catalog-skus";
const AMAZON_MAPPINGS_URL = "/api/v1/product-catalog/amazon-sku-mapping";

export const fetchInventorySkuMappings = createAsyncThunk(
    "inventorySkuMapping/fetchList",
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get(LIST_URL);
            return Array.isArray(data?.items) ? data.items : [];
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || "Failed to load mappings.";
            return rejectWithValue(typeof msg === "string" ? msg : "Failed to load mappings.");
        }
    }
);

export const fetchInventorySkuMappingStats = createAsyncThunk(
    "inventorySkuMapping/fetchStats",
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get(STATS_URL);
            return {
                mapped: Number(data?.mapped) || 0,
                unmapped: Number(data?.unmapped) || 0,
            };
        } catch (e) {
            return rejectWithValue(e?.response?.data?.error || e?.message);
        }
    }
);

/**
 * PATCH mapping row (`supply_product_id`, `notes`, etc.).
 * On success, callers typically refetch the list (enriched execution/supply fields from list endpoint).
 */
export const updateInventorySkuMapping = createAsyncThunk(
    "inventorySkuMapping/patch",
    async ({ id, body }, { rejectWithValue }) => {
        try {
            const { data } = await axios.patch(patchMappingUrl(id), body);
            return { id, item: data?.item };
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || "Update failed.";
            return rejectWithValue(typeof msg === "string" ? msg : "Update failed.");
        }
    }
);

export const deleteInventorySkuMapping = createAsyncThunk(
    "inventorySkuMapping/delete",
    async (id, { rejectWithValue }) => {
        try {
            await axios.delete(patchMappingUrl(id));
            return id;
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || "Delete failed.";
            return rejectWithValue(typeof msg === "string" ? msg : "Delete failed.");
        }
    }
);

const DB_PRODUCT_URL = "/api/v1/inventory-sku-mapping/db-product";

export const createDbOnlyProduct = createAsyncThunk(
    "inventorySkuMapping/createDbOnlyProduct",
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axios.post(DB_PRODUCT_URL, payload);
            return data?.product ?? null;
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || "Could not create product.";
            return rejectWithValue(typeof msg === "string" ? msg : "Could not create product.");
        }
    }
);

export const createInventorySkuMapping = createAsyncThunk(
    "inventorySkuMapping/create",
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axios.post(LIST_URL, payload);
            return data?.item ?? null;
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || "Could not add mapping.";
            return rejectWithValue(typeof msg === "string" ? msg : "Could not add mapping.");
        }
    }
);

/** Catalog SKU search (execution / supply pickers in mapping UI). Params match `catalog-skus` query string. */
export const fetchCatalogSkus = createAsyncThunk(
    "inventorySkuMapping/fetchCatalogSkus",
    async (params, { rejectWithValue }) => {
        try {
            const { data } = await axios.get(CATALOG_SKUS_URL, { params });
            return Array.isArray(data?.items) ? data.items : [];
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || "Search failed.";
            return rejectWithValue(typeof msg === "string" ? msg : "Search failed.");
        }
    }
);

export const fetchAmazonSkuMappings = createAsyncThunk(
    "inventorySkuMapping/fetchAmazonMappings",
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axios.get(AMAZON_MAPPINGS_URL);
            return Array.isArray(data?.items) ? data.items : [];
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || "Failed to load Amazon mappings.";
            return rejectWithValue(typeof msg === "string" ? msg : "Failed to load Amazon mappings.");
        }
    }
);

const initialState = {
    items: [],
    listLoading: false,
    listError: null,
    amazonItems: [],
    amazonLoading: false,
    amazonError: null,
    /** `{ mapped, unmapped }` — `null` until first successful stats fetch */
    stats: null,
    statsLoading: false,
};

const inventorySkuMappingSlice = createSlice({
    name: "inventorySkuMapping",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchInventorySkuMappings.pending, (state) => {
                state.listLoading = true;
                state.listError = null;
            })
            .addCase(fetchInventorySkuMappings.fulfilled, (state, action) => {
                state.listLoading = false;
                state.items = action.payload;
            })
            .addCase(fetchInventorySkuMappings.rejected, (state, action) => {
                state.listLoading = false;
                state.listError = action.payload || action.error?.message || null;
                state.items = [];
            })
            .addCase(fetchAmazonSkuMappings.pending, (state) => {
                state.amazonLoading = true;
                state.amazonError = null;
            })
            .addCase(fetchAmazonSkuMappings.fulfilled, (state, action) => {
                state.amazonLoading = false;
                state.amazonItems = action.payload;
            })
            .addCase(fetchAmazonSkuMappings.rejected, (state, action) => {
                state.amazonLoading = false;
                state.amazonError = action.payload || action.error?.message || null;
                state.amazonItems = [];
            })
            .addCase(fetchInventorySkuMappingStats.pending, (state) => {
                state.statsLoading = true;
            })
            .addCase(fetchInventorySkuMappingStats.fulfilled, (state, action) => {
                state.statsLoading = false;
                state.stats = action.payload;
            })
            .addCase(fetchInventorySkuMappingStats.rejected, (state) => {
                state.statsLoading = false;
                state.stats = { mapped: 0, unmapped: 0 };
            })
            .addCase(deleteInventorySkuMapping.fulfilled, (state, action) => {
                state.items = state.items.filter((item) => item.id !== action.payload);
            });
    },
});

export const selectInventorySkuMappingItems = (state) => state.inventorySkuMapping.items;
export const selectInventorySkuMappingListLoading = (state) => state.inventorySkuMapping.listLoading;
export const selectAmazonSkuMappingItems = (state) => state.inventorySkuMapping.amazonItems;
export const selectAmazonSkuMappingLoading = (state) => state.inventorySkuMapping.amazonLoading;
export const selectInventorySkuMappingStats = (state) => state.inventorySkuMapping.stats;
export const selectInventorySkuMappingStatsLoading = (state) => state.inventorySkuMapping.statsLoading;

export default inventorySkuMappingSlice.reducer;
